/**
 * useSmAimChain — the Selective-Menu AIM leg, packaged for reuse.
 *
 * Mirrors WinForms `ResidentDietOrderManager.vb:216` and its
 * caller chain: after the MP/Adj AIM dialog closes, the SM AIM
 * engine runs and either surfaces a review dialog for the user
 * to accept/reject per-day changes, or terminates with a toast.
 *
 * Consumers:
 *   • ResidentDetail (patient-profile Save + Apply-Now)
 *   • DietHistoryTab (Diet Fluctuations Add / Modify)
 *
 * Usage:
 *   const { armSmAim, runSmAim, smDialog } = useSmAimChain({
 *     facilityId, patientId, patientFullName,
 *     onFinished: () => { ...drain queued nav... },
 *   });
 *
 *   // Right before firing MP/Adj AIM:
 *   armSmAim(beforeTuple, afterTuple);
 *
 *   // From the MP/Adj dialog's onClose (or the no-proposals
 *   // branch when MP/Adj skips its own dialog):
 *   runSmAim();
 *
 *   // Once in the JSX tree:
 *   {smDialog}
 *
 * The hook owns:
 *   • the (before, after) stash between MP/Adj arming and SM firing
 *   • the SM mutation kickoff + terminal toast
 *   • the review-dialog mount (loading + result branches)
 *   • the modal backdrop that blocks page interactions while
 *     the engine runs
 *
 * `onFinished` fires at every tail of the chain (empty tuples,
 * no-proposals, error, dialog closed) so callers can drain queued
 * navigation without duplicating logic per call site.
 */
import { useCallback, useRef, useState, type ReactNode } from 'react';
import { useNotificationStore } from '../../stores/notificationStore';
import {
  useAimSelectiveMenuRun,
  type AimSMRunResponse,
  type AimSMChangeInput,
} from '../../api/aimSelectiveMenuApi';
import type { AimDietFlucsInput } from '../../api/aimDietApi';
import AimSelectiveMenuDialog from './AimSelectiveMenuDialog';

export interface UseSmAimChainOptions {
  facilityId: number;
  patientId: number;
  patientFullName: string;
  /** Called at the tail of the SM chain (no proposals / error /
   *  after user closes review dialog / empty tuples). Use to
   *  drain any queued navigation or trigger follow-up work.
   *  Optional — omit when the caller has nothing to do on tail. */
  onFinished?: () => void;
}

export interface UseSmAimChainResult {
  /** Stash diet-flucs before/after tuples + optional MP/Adj
   *  changes so the SM chain has the same diff to consume when
   *  triggered. Call this right BEFORE firing the MP/Adj
   *  `aimDietRun` mutation. `changes` folds the session
   *  accumulator into the final SM payload so a Save that both
   *  changes the diet AND has queued MP/Adj rows produces ONE
   *  regeneration call. */
  armSmAim: (
    before: AimDietFlucsInput,
    after: AimDietFlucsInput,
    changes?: AimSMChangeInput[],
  ) => void;
  /** Fire the SM AIM leg using the stashed tuples + changes.
   *  Call from the MP/Adj dialog's `onClose`, or directly from
   *  the no-proposals branch where MP/Adj skips its own dialog. */
  runSmAim: () => void;
  /** No-diet-change variant: fire the SM chain with an
   *  accumulated list of per-row MP/Adjustment changes only.
   *  Pass distinct before/after tuples if the caller has them
   *  (unified header button path); pass same-tuple twice for
   *  the pure MP/Adj-only path. */
  runSmAimWithChanges: (
    before: AimDietFlucsInput,
    after: AimDietFlucsInput,
    changes: AimSMChangeInput[],
  ) => void;
  /** Render this element once anywhere in the component tree.
   *  It's the SM review dialog (or its loading state). */
  smDialog: ReactNode;
}

export function useSmAimChain(opts: UseSmAimChainOptions): UseSmAimChainResult {
  const { facilityId, patientId, patientFullName, onFinished } = opts;
  const aimSelectiveMenuRun = useAimSelectiveMenuRun();
  const addNotification = useNotificationStore((s) => s.addNotification);

  const [smAimResult, setSmAimResult] = useState<AimSMRunResponse | null>(null);
  const [smAimLoading, setSmAimLoading] = useState(false);
  const pendingTuplesRef = useRef<{
    before: AimDietFlucsInput;
    after: AimDietFlucsInput;
    changes?: AimSMChangeInput[];
  } | null>(null);

  const armSmAim = useCallback((
    before: AimDietFlucsInput,
    after: AimDietFlucsInput,
    changes?: AimSMChangeInput[],
  ) => {
    pendingTuplesRef.current = { before, after, changes };
  }, []);

  // Shared kickoff — pulls out the identical .then/.catch
  // handling so the diet-tuple path (runSmAim) and the
  // MP/Adj-changes path (runSmAimWithChanges) don't duplicate
  // the terminal-toast + onFinished plumbing.
  const kickoffSmAim = useCallback((
    before: AimDietFlucsInput,
    after: AimDietFlucsInput,
    changes?: AimSMChangeInput[],
    /** Toast message used when the engine returns no proposals.
     *  Diet-tuple path says "diet change applied"; MP/Adj-tab
     *  path says "meal-pattern change applied" — because on the
     *  MP path the user just left the MP tab, not the diet
     *  order editor. */
    noProposalsMessage: string = 'Diet change applied. No selective menu updates needed.',
  ) => {
    if (!patientId || patientId <= 0) {
      onFinished?.();
      return;
    }
    setSmAimLoading(true);
    aimSelectiveMenuRun
      .mutateAsync({
        facilityId,
        patientId,
        rowDietFlucsOriginal: before,
        rowDietFlucsModified: after,
        changes,
      })
      .then((smRes) => {
        // hasProposals=true → transition the (already-open)
        // dialog from spinner to the review grid.
        // No proposals → close, terminal toast, tail callback.
        if (smRes.hasProposals) {
          setSmAimResult(smRes);
          setSmAimLoading(false);
        } else {
          setSmAimLoading(false);
          addNotification({
            message: noProposalsMessage,
            type: 'success',
            duration: 6000,
          });
          onFinished?.();
        }
      })
      .catch((err: Error) => {
        setSmAimLoading(false);
        addNotification({
          message: `Selective Menu analysis failed: ${err.message}`,
          type: 'error',
          duration: 10000,
        });
        onFinished?.();
      });
  }, [aimSelectiveMenuRun, facilityId, patientId, addNotification, onFinished]);

  const runSmAim = useCallback(() => {
    const tuples = pendingTuplesRef.current;
    if (!tuples) {
      onFinished?.();
      return;
    }
    pendingTuplesRef.current = null;
    kickoffSmAim(tuples.before, tuples.after, tuples.changes);
  }, [kickoffSmAim, onFinished]);

  const runSmAimWithChanges = useCallback((
    before: AimDietFlucsInput,
    after: AimDietFlucsInput,
    changes: AimSMChangeInput[],
  ) => {
    if (!changes || changes.length === 0) {
      // Only fire if we have MP/Adj changes to send. Diet-only
      // deltas already flow through `armSmAim + runSmAim`.
      onFinished?.();
      return;
    }
    kickoffSmAim(before, after, changes,
      'Meal-pattern change applied. No selective menu updates needed.');
  }, [kickoffSmAim, onFinished]);

  const smDialog = (
    <AimSelectiveMenuDialog
      open={smAimLoading || smAimResult !== null}
      loading={smAimLoading}
      facilityId={facilityId}
      patientId={patientId}
      patientFullName={patientFullName}
      result={smAimResult}
      onClose={() => {
        setSmAimResult(null);
        onFinished?.();
      }}
    />
  );

  return { armSmAim, runSmAim, runSmAimWithChanges, smDialog };
}
